'use client';

import * as React from 'react';
import { toast } from '@/lib/toast';
import { useWorkflow, type BatchActionItem } from '@/components/ui/workflow';
import type {
  BackendCorrespondence,
  CorrespondenceDraft,
  WorkflowDocument,
  WorkflowInstitution
} from '@/lib/services/correspondences/workflows/workflow-types';
import type { Attachment } from '@/lib/services/correspondences/types';
import type { LawFirmUser } from '@/lib/services/account/types';
import type { FileType } from '@/lib/services/files/types';
import WorkflowDataService from '@/lib/services/correspondences/workflows/workflow-data.service';
import { useFileUpload } from '@/lib/services/files/hooks';
import { useMatter } from '@/lib/services/matters/hooks';
import {
  emailsMatch,
  useOutlookMatterSender
} from '@/lib/services/integrations/outlook';
import { resolveSenderSignatoryDisclosure } from '@/components/shared/sender-signatory-disclosure';
import { getUserDisplayName } from '@/lib/utils/permissions';
import { calculateStats } from '../../notifications-workflow/utils';
import { useSendingStatus } from '../../notifications-workflow/hooks/use-sending-status';
import type {
  BaseSendPayload,
  SendInstitutionData,
  SendStepProps
} from './send-step.types';

export function useSendStep<TPayload extends BaseSendPayload>({
  config,
  initialData,
  onSend,
  onRefreshCorrespondences,
  isSending: externalIsSending,
  sendResults,
  matterId
}: SendStepProps<TPayload>) {
  const { getStepData, setStepData } = useWorkflow();

  const institutions = React.useMemo(
    () => getStepData('institutions') || initialData?.institutions || [],
    [getStepData, initialData?.institutions]
  );

  const selectedInstitutions = React.useMemo(
    () =>
      institutions
        .map((inst: WorkflowInstitution) => ({
          ...inst,
          selected: typeof inst.selected !== 'undefined' ? inst.selected : true
        }))
        .filter((inst: WorkflowInstitution) => inst.selected),
    [institutions]
  );

  const signatories = React.useMemo(
    () => initialData?.signatories || [],
    [initialData?.signatories]
  );
  const documents = React.useMemo(
    () => initialData?.documents || [],
    [initialData?.documents]
  );
  const correspondences = React.useMemo(
    () => initialData?.correspondences || [],
    [initialData?.correspondences]
  );

  const [isCreatingDrafts, setIsCreatingDrafts] = React.useState(false);
  const [localPendingResearchIds, setLocalPendingResearchIds] = React.useState<
    Set<number>
  >(() => new Set());
  const [userLetterContent, setUserLetterContent] = React.useState<
    Record<string, string>
  >({});
  const [userEmailSubjects, setUserEmailSubjects] = React.useState<
    Record<string, string>
  >({});
  const [userEmailBodies, setUserEmailBodies] = React.useState<
    Record<string, string>
  >({});
  const [emailTos, setEmailTos] = React.useState<Record<string, string>>({});
  const [additionalDocs, setAdditionalDocs] = React.useState<
    Record<string, WorkflowDocument[]>
  >({});
  const [masterSignatoryId, setMasterSignatoryId] = React.useState<string>('');
  const [sendSenderCopy, setSendSenderCopy] = React.useState<boolean>(true);
  // The self-copy is only redundant when the send leaves from the viewer's own
  // mailbox and so lands in their own Sent Items. Shared-mailbox mode and SES
  // (including a lawyer whose mailbox has never connected) keep it. The answer
  // is the matter's backend-resolved identity, the same one the send uses
  // (VALE-1873); the backend also drops the copy on it.
  const { data: matter } = useMatter(matterId);
  const outlookSender = useOutlookMatterSender(matter?.outlookSendIdentity);
  const effectiveSendSenderCopy = outlookSender.sendsFromCurrentUser
    ? false
    : sendSenderCopy;
  // Signatory ≠ sender disclosure (LF-5664). Banks and registries increasingly
  // ring the solicitor to authenticate correspondence, so the person sending
  // must be able to see that the letter will carry someone else's signature
  // over someone else's address. Disclosure only — never blocks the send.
  const masterSignatory = signatories.find(
    (s: LawFirmUser) => s.id.toString() === masterSignatoryId
  );
  const senderSignatoryDisclosure = outlookSender.active
    ? resolveSenderSignatoryDisclosure({
        senderName: outlookSender.senderName,
        signatoryName: masterSignatory
          ? getUserDisplayName(masterSignatory)
          : null,
        sameIdentity: emailsMatch(
          outlookSender.senderEmail,
          masterSignatory?.email
        )
      })
    : null;
  const [localCorrespondenceOverrides, setLocalCorrespondenceOverrides] =
    React.useState<Record<number, BackendCorrespondence>>({});

  const { sendingStatus, isSendingAll, setIsSendingAll, updateStatus } =
    useSendingStatus();

  const { uploadFile } = useFileUpload(matterId);

  const correspondenceType = config.correspondenceType;
  const draftErrorMessage = config.draftErrorMessage;

  const createInitialBulkDrafts = React.useEffectEvent(() => {
    const createBulkDrafts = async () => {
      if (selectedInstitutions.length > 0) {
        setIsCreatingDrafts(true);
        try {
          const existingInstitutionIds = new Set(
            correspondences
              .filter((c) => c.type === correspondenceType)
              .map((c) => c.institution.id)
          );
          const institutionIds = selectedInstitutions
            .map((inst: WorkflowInstitution) => inst.id)
            .filter((id: number) => !existingInstitutionIds.has(id));

          if (institutionIds.length === 0) {
            if (onRefreshCorrespondences) {
              await onRefreshCorrespondences();
            }
            setIsCreatingDrafts(false);
            return;
          }

          const result = await WorkflowDataService.createBulkDraftsAction(
            matterId,
            correspondenceType,
            institutionIds
          );

          if (!result.success) {
            throw new Error(result.error || 'Failed to create drafts');
          }

          if (onRefreshCorrespondences) {
            await onRefreshCorrespondences();
          }
        } catch (error) {
          console.error('Failed to create bulk drafts:', error);
          toast.error(draftErrorMessage);
        } finally {
          setIsCreatingDrafts(false);
        }
      }
    };

    createBulkDrafts();
  });
  React.useEffect(() => {
    createInitialBulkDrafts();
  }, []);

  const correspondenceByInstitution = React.useMemo(() => {
    const map: Record<number, BackendCorrespondence> = {};
    correspondences.forEach((corr) => {
      if (corr.type === correspondenceType && corr.institution?.id != null) {
        map[corr.institution.id] = corr;
      }
    });
    Object.entries(localCorrespondenceOverrides).forEach(
      ([instId, partial]) => {
        const id = Number(instId);
        if (map[id]) {
          map[id] = {
            ...map[id],
            ...(partial.validation && { validation: partial.validation }),
            ...(partial.attachments && { attachments: partial.attachments })
          };
        }
      }
    );
    return map;
  }, [correspondences, localCorrespondenceOverrides, correspondenceType]);

  const lastCorrespondencesRef = React.useRef(correspondences);
  if (lastCorrespondencesRef.current !== correspondences) {
    lastCorrespondencesRef.current = correspondences;
    if (Object.keys(localCorrespondenceOverrides).length > 0) {
      setLocalCorrespondenceOverrides({});
    }
  }

  const letterContent = React.useMemo<Record<string, string>>(() => {
    const result: Record<string, string> = {};
    for (const inst of selectedInstitutions as WorkflowInstitution[]) {
      const key = inst.id.toString();
      result[key] =
        userLetterContent[key] ??
        correspondenceByInstitution[inst.id]?.correspondenceLetterBody ??
        '';
    }
    return result;
  }, [selectedInstitutions, correspondenceByInstitution, userLetterContent]);
  const emailSubjects = React.useMemo<Record<string, string>>(() => {
    const result: Record<string, string> = {};
    for (const inst of selectedInstitutions as WorkflowInstitution[]) {
      const key = inst.id.toString();
      result[key] =
        userEmailSubjects[key] ??
        correspondenceByInstitution[inst.id]?.subject ??
        '';
    }
    return result;
  }, [selectedInstitutions, correspondenceByInstitution, userEmailSubjects]);
  const emailBodies = React.useMemo<Record<string, string>>(() => {
    const result: Record<string, string> = {};
    for (const inst of selectedInstitutions as WorkflowInstitution[]) {
      const key = inst.id.toString();
      result[key] =
        userEmailBodies[key] ??
        correspondenceByInstitution[inst.id]?.fragments?.mainContent ??
        '';
    }
    return result;
  }, [selectedInstitutions, correspondenceByInstitution, userEmailBodies]);

  const stats = React.useMemo(() => {
    const items = selectedInstitutions.map((inst: WorkflowInstitution) => {
      const correspondence = correspondenceByInstitution[inst.id];
      return {
        id: inst.id.toString(),
        status: correspondence?.validation.isValid ? 'pending' : 'blocked'
      };
    });
    return calculateStats(items as BatchActionItem[]);
  }, [selectedInstitutions, correspondenceByInstitution]);

  const allCorrespondencesValid = React.useMemo(() => {
    return selectedInstitutions.every((inst: WorkflowInstitution) => {
      const correspondence = correspondenceByInstitution[inst.id];
      return correspondence?.validation.isValid || false;
    });
  }, [selectedInstitutions, correspondenceByInstitution]);

  // When supportsPostOnly is false, no correspondence ever has sendViaPostOnly,
  // so these checks become no-ops — same logic for both flows.
  const allSelectedArePostOnly = React.useMemo(() => {
    if (!config.supportsPostOnly) return false;
    if (selectedInstitutions.length === 0) return false;
    return selectedInstitutions.every((inst: WorkflowInstitution) => {
      const correspondence = correspondenceByInstitution[inst.id];
      return correspondence?.sendViaPostOnly === true;
    });
  }, [
    selectedInstitutions,
    correspondenceByInstitution,
    config.supportsPostOnly
  ]);

  const allSelected = React.useMemo(() => {
    const selectableInstitutions = institutions.filter(
      (inst: WorkflowInstitution) => {
        const correspondence = correspondenceByInstitution[inst.id];
        return !correspondence?.sendViaPostOnly;
      }
    );
    if (selectableInstitutions.length === 0) return false;
    return selectableInstitutions.every(
      (inst: WorkflowInstitution) => inst.selected !== false
    );
  }, [institutions, correspondenceByInstitution]);

  const buildSendInstitution = React.useCallback(
    (id: string): SendInstitutionData => {
      const instId = parseInt(id);
      const correspondence = correspondenceByInstitution[instId];
      return {
        id,
        letterContent: letterContent[id] || '',
        emailSubject: emailSubjects[id],
        emailBody: emailBodies[id],
        emailTo: emailTos[id] || '',
        attachments: (additionalDocs[id] || []).map(
          (doc: WorkflowDocument) => doc.id
        ),
        signatoryUserId: correspondence?.signatoryUser?.email
          ? signatories.find(
              (s) => s.email === correspondence.signatoryUser!.email
            )?.id
          : undefined
      };
    },
    [
      additionalDocs,
      correspondenceByInstitution,
      emailBodies,
      emailSubjects,
      emailTos,
      letterContent,
      signatories
    ]
  );

  /**
   * `onSend` is a required prop, so this is unreachable from typed code — but a
   * JS call site, a widened type or an `any` cast could still render the step
   * without one. Fail loudly and mark the items errored: the one thing this
   * surface must never do is report a send it did not perform (VALE-546).
   */
  const reportMissingSendHandler = (ids: string[]) => {
    console.error(
      'Send step reached with no onSend handler. Nothing was sent.'
    );
    toast.error(
      'Unable to send: this workflow has no send handler configured.'
    );
    for (const id of ids) {
      updateStatus(id, 'error');
    }
  };

  const handleSendSingle = async (item: BatchActionItem) => {
    if (!onSend) {
      reportMissingSendHandler([item.id]);
      return;
    }

    const institutionData = selectedInstitutions.find(
      (inst: WorkflowInstitution) => inst.id.toString() === item.id
    );
    if (!institutionData) return;

    const correspondence = correspondenceByInstitution[parseInt(item.id)];

    try {
      updateStatus(item.id, 'loading');
      await onSend({
        institutions: [institutionData],
        sendData: {
          institutions: [buildSendInstitution(item.id)],
          sendSenderCopy: effectiveSendSenderCopy
        }
      } as TPayload);

      const isSendViaPostOnly = correspondence?.sendViaPostOnly === true;
      const isResearch =
        !isSendViaPostOnly &&
        !institutionData.emailAddress &&
        !emailTos[item.id];
      if (isResearch) {
        setLocalPendingResearchIds((prev) =>
          new Set(prev).add(parseInt(item.id))
        );
      } else {
        updateStatus(item.id, 'success');
      }

      onRefreshCorrespondences?.();
    } catch (_error) {
      updateStatus(item.id, 'error');
    }
  };

  const handleSendAll = async () => {
    if (!onSend) {
      reportMissingSendHandler(
        selectedInstitutions.map((inst: WorkflowInstitution) =>
          inst.id.toString()
        )
      );
      return;
    }

    if (!allCorrespondencesValid) {
      alert(
        'Cannot send: Some correspondences have validation errors. Please check the errors below.'
      );
      return;
    }

    const institutionsToSend = selectedInstitutions.filter(
      (inst: WorkflowInstitution) => {
        const correspondence = correspondenceByInstitution[inst.id];
        return (
          !correspondence?.sendViaPostOnly &&
          correspondence?.status !== 'pending-research-email' &&
          !localPendingResearchIds.has(inst.id)
        );
      }
    );

    if (institutionsToSend.length === 0) {
      toast.info(config.nothingToSendMessage);
      return;
    }

    setIsSendingAll(true);

    try {
      for (const inst of institutionsToSend) {
        updateStatus(inst.id.toString(), 'loading');
      }

      await onSend({
        institutions: institutionsToSend,
        sendData: {
          institutions: institutionsToSend.map((inst: WorkflowInstitution) =>
            buildSendInstitution(inst.id.toString())
          ),
          sendSenderCopy: effectiveSendSenderCopy
        }
      } as TPayload);

      const researchIds: number[] = [];
      for (const inst of institutionsToSend) {
        const hasEmail = inst.emailAddress || emailTos[inst.id.toString()];
        if (hasEmail) {
          updateStatus(inst.id.toString(), 'success');
        } else {
          researchIds.push(inst.id);
        }
      }
      if (researchIds.length > 0) {
        setLocalPendingResearchIds((prev) => {
          const next = new Set(prev);
          researchIds.forEach((id) => next.add(id));
          return next;
        });
      }

      onRefreshCorrespondences?.();
    } catch (error) {
      console.error('Send all failed:', error);
      for (const inst of institutionsToSend) {
        updateStatus(inst.id.toString(), 'error');
      }
    }

    setIsSendingAll(false);
  };

  React.useEffect(() => {
    if (sendResults) {
      sendResults.successful.forEach((id) =>
        updateStatus(id.toString(), 'success')
      );
      sendResults.failed.forEach(({ id, error: _error }) =>
        updateStatus(id.toString(), 'error')
      );
    }
  }, [sendResults, updateStatus]);

  const handleUpdateLetterContent = (
    institutionId: string,
    content: string,
    emailSubject?: string,
    emailBody?: string
  ) => {
    setUserLetterContent((prev) => ({ ...prev, [institutionId]: content }));
    if (emailSubject !== undefined) {
      setUserEmailSubjects((prev) => ({
        ...prev,
        [institutionId]: emailSubject
      }));
    }
    if (emailBody !== undefined) {
      setUserEmailBodies((prev) => ({
        ...prev,
        [institutionId]: emailBody
      }));
    }
  };

  const handleEmailToChange = (institutionId: string, emailTo: string) => {
    setEmailTos((prev) => ({ ...prev, [institutionId]: emailTo }));
  };

  const handleAddDocument = (institutionId: string, doc: WorkflowDocument) => {
    setAdditionalDocs((prev) => ({
      ...prev,
      [institutionId]: [...(prev[institutionId] || []), doc]
    }));
  };

  const handleAddCorrespondenceAttachment = async (
    institutionId: string,
    doc: WorkflowDocument
  ) => {
    if (!matterId) return;

    try {
      const correspondence =
        correspondenceByInstitution[parseInt(institutionId)];
      if (!correspondence) {
        toast.error(
          'Correspondence not ready. Please wait for workflow to fully load.'
        );
        return;
      }

      const currentAttachmentIds = correspondence.attachments.map(
        (att: Attachment) => att.id
      );

      if (currentAttachmentIds.includes(doc.id)) {
        toast.info('Document is already attached');
        return;
      }

      const updated = await WorkflowDataService.updateDraft(
        matterId,
        correspondence.id,
        {
          attachments: [...currentAttachmentIds, doc.id]
        } as unknown as Partial<CorrespondenceDraft>
      );

      if (updated) {
        setLocalCorrespondenceOverrides((prev) => ({
          ...prev,
          [correspondence.institution.id]: updated
        }));
      }

      if (onRefreshCorrespondences) {
        await onRefreshCorrespondences();
      }

      toast.success('Attachment added successfully');
    } catch (error) {
      console.error('Failed to add attachment:', error);
      toast.error(
        'Failed to add attachment: ' +
          (error instanceof Error ? error.message : 'Unknown error')
      );
    }
  };

  const handleRemoveDocument = (institutionId: string, docId: number) => {
    setAdditionalDocs((prev) => ({
      ...prev,
      [institutionId]: (prev[institutionId] || []).filter((d) => d.id !== docId)
    }));
  };

  const handleRemoveCorrespondenceAttachment = async (
    institutionId: string,
    docId: number
  ) => {
    if (!matterId) return;

    try {
      const correspondence =
        correspondenceByInstitution[parseInt(institutionId)];
      if (!correspondence) {
        console.error(
          'No correspondence found for institution:',
          institutionId
        );
        return;
      }

      const updatedAttachments = correspondence.attachments.filter(
        (att: Attachment) => att.id !== docId
      );

      const updated = await WorkflowDataService.updateDraft(
        matterId,
        correspondence.id,
        {
          attachments: updatedAttachments.map((att: Attachment) => att.id)
        } as unknown as Partial<CorrespondenceDraft>
      );

      if (updated) {
        setLocalCorrespondenceOverrides((prev) => ({
          ...prev,
          [correspondence.institution.id]: updated
        }));
      }

      if (onRefreshCorrespondences) {
        await onRefreshCorrespondences();
      }
    } catch (error) {
      console.error('Failed to remove attachment:', error);
      toast.error('Failed to remove attachment');
    }
  };

  const handleUploadDocument = async (
    institutionId: string,
    files: Array<{
      file: File;
      fileType: string;
      isCertifiedCopy: boolean;
    }>
  ) => {
    if (!matterId) return;

    try {
      const correspondence =
        correspondenceByInstitution[parseInt(institutionId)];
      if (!correspondence) {
        console.error(
          'No correspondence found for institution:',
          institutionId
        );
        throw new Error('No correspondence found for this institution');
      }

      for (const uploadItem of files) {
        const result = await uploadFile(
          uploadItem.file,
          uploadItem.fileType as FileType,
          {
            matterId,
            displayName: uploadItem.file.name,
            isCertifiedCopy: uploadItem.isCertifiedCopy,
            attachmentId: correspondence.id
          }
        );

        if (!result) {
          throw new Error('Upload failed');
        }

        const currentAttachmentIds = correspondence.attachments.map(
          (att: Attachment) => att.id
        );

        if (!currentAttachmentIds.includes(result.id)) {
          const updated = await WorkflowDataService.updateDraft(
            matterId,
            correspondence.id,
            {
              attachments: [...currentAttachmentIds, result.id]
            } as unknown as Partial<CorrespondenceDraft>
          );

          if (updated) {
            setLocalCorrespondenceOverrides((prev) => ({
              ...prev,
              [correspondence.institution.id]: updated
            }));
          }
        }
      }

      if (onRefreshCorrespondences) {
        await onRefreshCorrespondences();
      }

      toast.success(
        files.length === 1
          ? 'Attachment uploaded successfully'
          : `${files.length} attachments uploaded successfully`
      );
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error(
        'Failed to upload attachment: ' +
          (error instanceof Error ? error.message : 'Unknown error')
      );
      throw error;
    }
  };

  const handleChangeSignatory = async (
    institutionId: string,
    signatoryId: string
  ) => {
    if (!matterId) return;

    try {
      const correspondence =
        correspondenceByInstitution[parseInt(institutionId)];
      if (!correspondence) {
        console.error(
          'No correspondence found for institution:',
          institutionId
        );
        return;
      }

      await WorkflowDataService.updateCorrespondenceSignatory(
        matterId,
        correspondence.id,
        signatoryId ? parseInt(signatoryId) : null
      );

      if (onRefreshCorrespondences) {
        await onRefreshCorrespondences();
      }
    } catch (error) {
      console.error('Failed to update signatory:', error);
    }
  };

  const handleToggleInstitution = (
    institutionId: string,
    selected: boolean
  ) => {
    const updatedInstitutions = institutions.map((inst: WorkflowInstitution) =>
      inst.id.toString() === institutionId ? { ...inst, selected } : inst
    );
    setStepData('institutions', updatedInstitutions);
  };

  const handleToggleAll = () => {
    const newSelectedState = !allSelected;
    const updatedInstitutions = institutions.map(
      (inst: WorkflowInstitution) => {
        const correspondence = correspondenceByInstitution[inst.id];
        if (correspondence?.sendViaPostOnly) {
          return inst;
        }
        return { ...inst, selected: newSelectedState };
      }
    );
    setStepData('institutions', updatedInstitutions);
  };

  const handleToggleSendViaPostOnly = async (
    institutionId: string,
    sendViaPostOnly: boolean
  ) => {
    if (!matterId) return;

    try {
      const correspondence =
        correspondenceByInstitution[parseInt(institutionId)];
      if (!correspondence) {
        console.error(
          'No correspondence found for institution:',
          institutionId
        );
        return;
      }

      await WorkflowDataService.updateDraft(matterId, correspondence.id, {
        sendViaPostOnly
      } as unknown as Partial<CorrespondenceDraft>);

      if (onRefreshCorrespondences) {
        await onRefreshCorrespondences();
      }
    } catch (error) {
      console.error('Failed to update sendViaPostOnly:', error);
      toast.error('Failed to update send via post only setting');
    }
  };

  const handleMasterSignatoryChange = async (value: string) => {
    setMasterSignatoryId(value);
    if (matterId) {
      try {
        const updatePromises = selectedInstitutions
          .map((inst: WorkflowInstitution) => {
            const correspondence = correspondenceByInstitution[inst.id];
            if (correspondence) {
              return WorkflowDataService.updateCorrespondenceSignatory(
                matterId,
                correspondence.id,
                value ? parseInt(value) : null
              );
            }
            return null;
          })
          .filter(Boolean);

        await Promise.all(updatePromises);

        if (onRefreshCorrespondences) {
          await onRefreshCorrespondences();
        }
      } catch (error) {
        console.error('Failed to update all signatories:', error);
        setMasterSignatoryId('');
      }
    }
  };

  return {
    institutions,
    selectedInstitutions,
    signatories,
    documents,
    correspondenceByInstitution,
    isCreatingDrafts,
    localPendingResearchIds,
    additionalDocs,
    emailTos,
    sendingStatus,
    isSendingAll,
    externalIsSending,
    masterSignatoryId,
    handleMasterSignatoryChange,
    sendSenderCopy,
    setSendSenderCopy,
    /**
     * True only when the send leaves from the viewer's own mailbox, so it lands
     * in their own Sent Items. The "Send me a copy" control is hidden on this,
     * never on "is Outlook active" (VALE-1873).
     */
    outlookSendsFromMe: outlookSender.sendsFromCurrentUser,
    /**
     * Non-null when the master signatory is not the resolved sending identity —
     * the letter carries one lawyer's signature over another's mailbox.
     */
    senderSignatoryDisclosure,
    stats,
    allCorrespondencesValid,
    allSelected,
    allSelectedArePostOnly,
    sendResults,
    initialMatterId: initialData?.matter?.id,
    handleSendSingle,
    handleSendAll,
    handleUpdateLetterContent,
    handleEmailToChange,
    handleAddDocument,
    handleAddCorrespondenceAttachment,
    handleRemoveDocument,
    handleRemoveCorrespondenceAttachment,
    handleUploadDocument,
    handleChangeSignatory,
    handleToggleInstitution,
    handleToggleAll,
    handleToggleSendViaPostOnly
  };
}

export type SendStepState = ReturnType<typeof useSendStep>;
